if (!customElements.get('promo-ticker')) {
  customElements.define('promo-ticker', class PromoTicker extends HTMLElement {
    static instances = new Set();
    static resizeListenerAttached = false;
    static resizeRaf = null;

    static handleGlobalResize() {
      if (PromoTicker.resizeRaf) return;
      PromoTicker.resizeRaf = requestAnimationFrame(() => {
        PromoTicker.resizeRaf = null;
        PromoTicker.instances.forEach((instance) => {
          if (!instance?.isConnected) return;
          instance.handleResize?.();
        });
      });
    }

    static attachResizeListener() {
      if (PromoTicker.resizeListenerAttached) return;
      window.addEventListener('resize', PromoTicker.handleGlobalResize);
      PromoTicker.resizeListenerAttached = true;
    }

    static detachResizeListenerIfIdle() {
      if (PromoTicker.instances.size > 0 || !PromoTicker.resizeListenerAttached) return;
      window.removeEventListener('resize', PromoTicker.handleGlobalResize);
      PromoTicker.resizeListenerAttached = false;
      if (PromoTicker.resizeRaf) {
        cancelAnimationFrame(PromoTicker.resizeRaf);
        PromoTicker.resizeRaf = null;
      }
    }

    constructor() {
      super();

      Object.assign(this, {
        minItemsCount: 2,
        lastContainerWidth: 0,
        lastItemWidth: 0,
        lastItemsCount: 0,
        lastWindowWidth: window.innerWidth,
        scrollStarted: false,
        isVisible: false,
        wasMobile: this.hasAttribute('data-mobile-disabled') && DeviceUtils.isMobileSmall,
        pendingUpdate: false,
        resizeTimeout: null,
        resizeDebounceDelay: 150,
        resizeRafId: null,
        measureRetryCount: 0,
        maxMeasureRetries: 8,
        measureRetryTimer: null,
        bootstrapped: false,
        measuredOnce: false,
        delayRafId: null,
        delayStartAt: 0,
        remainingDelay: null,
        focusFrozen: false,
        focusedChild: null,
        lastFocusedItem: null
      });

      this.handleResize = () => {
        const currentWindowWidth = window.innerWidth;
        if (currentWindowWidth === this.lastWindowWidth) return;
        this.lastWindowWidth = currentWindowWidth;

        clearTimeout(this.resizeTimeout);

        this.resizeTimeout = setTimeout(() => {
          const hasMobileAttr = this.hasAttribute('data-mobile-disabled');
          const isMobile = DeviceUtils.isMobileSmall;

          if (hasMobileAttr) {
            if (isMobile && !this.wasMobile) {
              const scrollContainer = this.querySelector('.ticker__track');
              if (scrollContainer) {
                const items = scrollContainer.querySelectorAll('.ticker__item');
                items.forEach((item, index) => {
                  if (index > 0) item.remove();
                });
              }
              this.classList.add('ticker--off-scroll');
              this.classList.remove('ticker--initialized');
              this.visibilityObserver?.disconnect();
              if (this.delayRafId) {
                cancelAnimationFrame(this.delayRafId);
                this.delayRafId = null;
              }
              this.wasMobile = true;
              this.pendingUpdate = false;
              this.scrollStarted = false;
              cancelAnimationFrame(this.updateRequestId);
              cancelAnimationFrame(this.resizeRafId);
              return;
            }

            if (!isMobile && this.wasMobile) {
              this.wasMobile = false;
              this.scrollStarted = false;
              this.classList.remove('ticker--off-scroll');

              cancelAnimationFrame(this.updateRequestId);
              cancelAnimationFrame(this.resizeRafId);
              this.pendingUpdate = false;

              this.lastContainerWidth = 0;
              this.lastItemWidth = 0;
              this.lastItemsCount = 0;

              this.updateRequestId = requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  this.updateItems();
                  this.observeVisibility();
                  if (this.isVisible) {
                    this.handleScrollDelay();
                  } else {
                    this.scrollStarted = true;
                  }
                });
              });
              return;
            }

            if (isMobile) return;
          }

          cancelAnimationFrame(this.resizeRafId);
          this.classList.add('ticker--off-scroll');

          this.resizeRafId = requestAnimationFrame(() => {
            this.updateItems();
            requestAnimationFrame(() => this.classList.remove('ticker--off-scroll'));
          });
        }, this.resizeDebounceDelay);
      };

      this.handleFocusIn = (e) => {
        const item = e.target.closest?.('.ticker__item');
        if (!item || !this.contains(item) || e.target === item) return;

        let directChild = e.target;
        while (directChild.parentElement !== item) {
          directChild = directChild.parentElement;
        }

        if (directChild === this.focusedChild) return;

        this.focusedChild = directChild;
        this.bringItemIntoView(directChild);
      };

      this.handleFocusOut = (e) => {
        if (this.contains(e.relatedTarget)) return;
        this.focusedChild = null;
        this.releaseFocusPause();
      };
    }

    connectedCallback() {
      PromoTicker.instances.add(this);
      PromoTicker.attachResizeListener();
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.bootstrap();
    }

    bootstrap() {
      if (this.bootstrapped) return;
      this.bootstrapped = true;

      const mobileDisabled = this.hasAttribute('data-mobile-disabled') && DeviceUtils.isMobileSmall;
      if (mobileDisabled) {
        this.classList.add('ticker--off-scroll');
        this.triggerAnimation();
        return;
      }

      this.handleScrollDelay();
      this.observeVisibility();
    }

    handleScrollDelay() {
      if (this.scrollStarted) return;
      const delay = parseInt(this.dataset.delay);
      const effectiveDelay = this.remainingDelay ?? delay;
      if (effectiveDelay && effectiveDelay > 0) {
        this.classList.add('ticker--off-scroll');
        if (this.delayRafId) {
          cancelAnimationFrame(this.delayRafId);
          this.delayRafId = null;
        }
        this.delayStartAt = performance.now();
        const waitForDelay = () => {
          if (!this.isConnected) return;
          if (performance.now() - this.delayStartAt >= effectiveDelay) {
            this.delayRafId = null;
            this.remainingDelay = 0;
            this.scrollStarted = true;
            if (this.isVisible) {
              this.classList.remove('ticker--off-scroll');
            }
            return;
          }
          this.delayRafId = requestAnimationFrame(waitForDelay);
        };
        this.delayRafId = requestAnimationFrame(waitForDelay);
      } else {
        this.remainingDelay = 0;
        this.scrollStarted = true;
        if (this.isVisible) {
          this.classList.remove('ticker--off-scroll');
        }
      }
    }

    observeVisibility() {
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
      }

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.isVisible = true;
            if (!this.measuredOnce) {
              this.updateItems();
              this.measuredOnce = true;
            }
            if (this.scrollStarted) {
              this.classList.remove('ticker--off-scroll');
            } else {
              this.classList.add('ticker--off-scroll');
              this.handleScrollDelay();
            }
          } else {
            this.isVisible = false;
            this.classList.add('ticker--off-scroll');
            if (this.delayRafId && !this.scrollStarted) {
              const initialDelay = parseInt(this.dataset.delay) || 0;
              const activeDelay = this.remainingDelay ?? initialDelay;
              const elapsed = performance.now() - this.delayStartAt;
              this.remainingDelay = Math.max(activeDelay - elapsed, 0);
              cancelAnimationFrame(this.delayRafId);
              this.delayRafId = null;
            }
          }
        });
      }, {
        threshold: 0,
        rootMargin: '300px 0px'
      });

      observer.observe(this);
      this.visibilityObserver = observer;
    }

    updateItems() {
      if (this.pendingUpdate) return;
      this.pendingUpdate = true;

      const scrollContainer = this.querySelector('.ticker__track');
      const originalItem = this.querySelector('.ticker__item');

      if (!scrollContainer || !originalItem) {
        this.pendingUpdate = false;
        return;
      }

      const itemWidth = originalItem.offsetWidth;
      const containerWidth = this.offsetWidth;

      if (!itemWidth || !containerWidth) {
        this.pendingUpdate = false;
        if (this.measureRetryCount >= this.maxMeasureRetries) {
          return;
        }
        this.measureRetryCount += 1;
        if (this.measureRetryTimer) clearTimeout(this.measureRetryTimer);
        this.measureRetryTimer = setTimeout(() => this.updateItems(), 120);
        return;
      }
      this.measureRetryCount = 0;

      const speedPerSec = parseInt(scrollContainer.dataset.speed) || 100;
      const itemsCount = Math.max(this.minItemsCount, Math.ceil(containerWidth / itemWidth) + 1);

      if (this.lastItemsCount === itemsCount &&
        this.lastContainerWidth === containerWidth &&
        this.lastItemWidth === itemWidth) {
        this.pendingUpdate = false;
        return;
      }

      [this.lastContainerWidth, this.lastItemWidth, this.lastItemsCount] = [containerWidth, itemWidth, itemsCount];
      this.style.setProperty('--ticker-speed', `${itemWidth / speedPerSec}s`);

      const currentItems = scrollContainer.querySelectorAll('.ticker__item');
      const diff = itemsCount - currentItems.length;

      if (diff > 0) {
        for (let i = 0; i < diff; i++) {
          const clone = originalItem.cloneNode(true);
          this.prepareClone(clone);
          scrollContainer.appendChild(clone);
        }
      } else if (diff < 0) {
        for (let i = 0; i < Math.abs(diff); i++) {
          const last = scrollContainer.querySelector('.ticker__item:last-child');
          if (last) last.remove();
        }
      }

      this.classList.add('ticker--initialized');
      this.pendingUpdate = false;
      this.triggerAnimation();
    }

    prepareClone(clone) {
      clone.querySelectorAll('[data-animate], [data-animate-item]').forEach(el => {
        el.style.removeProperty('opacity');
        el.style.removeProperty('--motion-translateY');
        el.style.removeProperty('transform');
        el.classList.remove('animations-end');
      });

      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]')
        .forEach(el => el.setAttribute('tabindex', '-1'));
    }

    bringItemIntoView(item) {
      const track = this.querySelector('.ticker__track');
      if (!track) return;

      if (this.focusFrozen && this.lastFocusedItem === item) return;

      const wasAlreadyFrozen = this.focusFrozen;
      const frozenBeforeThisCall = getComputedStyle(track).transform;

      if (!wasAlreadyFrozen) {
        track.style.animation = 'none';
        track.style.transform = frozenBeforeThisCall === 'none' ? 'translateX(0px)' : frozenBeforeThisCall;
      }

      const containerRect = this.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const pad = 12;
      const padRight = 30;

      let delta = 0;
      if (itemRect.left < containerRect.left) {
        delta = containerRect.left - itemRect.left + pad;
      } else if (itemRect.right > containerRect.right) {
        delta = (containerRect.right - itemRect.right) - padRight;
      }

      if (delta === 0) {
        this.lastFocusedItem = item;

        if (!wasAlreadyFrozen) {
          track.style.transition = '';
          track.style.transform = '';
          track.style.animation = '';
        }
        return;
      }

      this.classList.add('ticker--off-scroll');
      this.focusFrozen = true;
      this.lastFocusedItem = item;

      requestAnimationFrame(() => {
        const matrix = new DOMMatrix(getComputedStyle(track).transform);
        matrix.e += delta;
        track.style.transition = 'transform 0.25s ease';
        track.style.transform = matrix.toString();
      });
    }

    releaseFocusPause() {
      if (!this.focusFrozen) return;
      this.focusFrozen = false;
      this.lastFocusedItem = null;

      const track = this.querySelector('.ticker__track');
      if (!track) return;

      track.style.transition = '';
      track.style.transform = '';
      track.style.animation = '';

      if (this.scrollStarted && this.isVisible) {
        this.classList.remove('ticker--off-scroll');
      }
    }

    triggerAnimation() {
      const animatedStages = this.closest('animated-stages');
      if (animatedStages && animatedStages.isPaused) {
        requestAnimationFrame(() => {
          animatedStages.play();
        });
      }
    }

    disconnectedCallback() {
      PromoTicker.instances.delete(this);
      PromoTicker.detachResizeListenerIfIdle();
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      cancelAnimationFrame(this.resizeRafId);
      cancelAnimationFrame(this.updateRequestId);
      if (this.delayRafId) {
        cancelAnimationFrame(this.delayRafId);
        this.delayRafId = null;
      }
      clearTimeout(this.resizeTimeout);
      clearTimeout(this.measureRetryTimer);
      this.visibilityObserver?.disconnect();
    }
  });
}